/**
 * File: theme_mode_context.tsx
 * Description: Defines the ThemeModeContext and its custom hook for consuming it.
 */

import { createContext, useContext } from "react";

/**
 * Shape of the value exposed by ThemeModeContext.
 *
 * @property {string} mode - Current color mode ("light" or "dark").
 * @property {() => "light" | "dark"} toggleColorMode - Toggles the color mode and returns the new value.
 */
interface ColorModeContextType {
  mode: string;
  toggleColorMode: () => "light" | "dark";
}

export const ThemeModeContext = createContext<ColorModeContextType | undefined>(undefined);

/**
 * Custom hook that returns the ThemeModeContext value.
 * Must be called inside a component wrapped by ThemeModeProvider.
 *
 * @returns {ColorModeContextType} The current color mode and its toggler.
 * @throws {Error} When called outside of a ThemeModeProvider tree.
 */
export const useThemeModeContext = () => {
  const context = useContext(ThemeModeContext);
  if (context === undefined) {
    throw new Error("useThemeModeContext must be used inside ThemeModeProvider");
  }
  return context;
};